import React, { useState } from 'react';
import { Form, Input, Button, message, Alert, Card, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import userService from '../../services/userService';
import { updateUserProfile } from '../../redux/userSlice';

const { Title, Paragraph } = Typography;

const ForceChangePassword = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    if (!currentUser.must_change_password) {
        return <Navigate to="/dashboard" replace />;
    }

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const response = await userService.changeRequiredPassword({
                current_password: values.current_password,
                password: values.password,
                password_confirmation: values.password_confirmation
            });

            if (response?.user) {
                dispatch(updateUserProfile(response.user));
            } else {
                dispatch(updateUserProfile({ must_change_password: false }));
            }

            message.success('Password updated successfully. You can now use the application.');
            navigate('/dashboard', { replace: true });
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
            <Card style={{ width: '100%', maxWidth: 480, borderRadius: 12 }}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img
                        src="/assets/images/logo.png"
                        alt="MaveshiBids"
                        style={{ height: 48, marginBottom: 12 }}
                    />
                    <Title level={3} style={{ marginBottom: 8 }}>Change Temporary Password</Title>
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Your account was created with a temporary password. Please set a new password before continuing.
                    </Paragraph>
                </div>

                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                    message="Password change required"
                    description={`Signed in as ${currentUser.email}`}
                />

                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item
                        name="current_password"
                        label="Current (Temporary) Password"
                        rules={[{ required: true, message: 'Please enter your temporary password' }]}
                    >
                        <Input.Password prefix={<LockOutlined />} size="large" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                            { required: true, message: 'Please enter a new password' },
                            { min: 8, message: 'Password must be at least 8 characters' }
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} size="large" />
                    </Form.Item>

                    <Form.Item
                        name="password_confirmation"
                        label="Confirm New Password"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Please confirm your new password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match'));
                                }
                            })
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} size="large" />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" loading={loading} block size="large">
                        Update Password & Continue
                    </Button>
                </Form>
            </Card>
        </div>
    );
};

export default ForceChangePassword;
